import React, { useState, useEffect } from 'react';
import { trackEvent } from '../utils/matomoEvents';

export default function DogImageGallery({
  images,
  showDogInfo = false, // Whether to display dog information below the image
  renderImageInfo, // Custom function to render additional image information
  showCloseButton = true, // Whether to show the close button in fullscreen mode
  showPlayButton = true, // Whether to show the play/pause button
  showMaximizeButton = true, // Whether to show the maximize button
  showNavigationButtons = true, // Whether to show navigation buttons (prev/next)
  onFullscreenToggle // Callback for when fullscreen mode is toggled
}) {
  const [currentIndex, setCurrentIndex] = useState(0); // Current image index
  const [isPlaying, setIsPlaying] = useState(false); // Slideshow play state
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen state

  // Enter fullscreen mode
  const enterFullscreen = () => {
    setIsFullscreen(true);
    if (onFullscreenToggle) onFullscreenToggle(true);
    // Track fullscreen event
    trackEvent('Gallery', 'EnterFullscreen', images[currentIndex]);
  };

  // Exit fullscreen mode
  const exitFullscreen = () => {
    setIsFullscreen(false);
    if (onFullscreenToggle) onFullscreenToggle(false);
    // Track exit fullscreen event
    trackEvent('Gallery', 'ExitFullscreen', images[currentIndex]);
  };

  // Navigate to the previous image
  const prevImage = () => {
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    // Track navigation event
    trackEvent('Gallery', 'NavigatePrevious', images[newIndex]);
  };

  // Navigate to the next image
  const nextImage = () => {
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    // Track navigation event
    trackEvent('Gallery', 'NavigateNext', images[newIndex]);
  };

  // Toggle slideshow play/pause state
  const togglePlay = () => {
    const newPlayState = !isPlaying;
    setIsPlaying(newPlayState);
    // Track play/pause event
    trackEvent('Gallery', newPlayState ? 'StartSlideshow' : 'PauseSlideshow', images[currentIndex]);
  };

  // Extract dog information from the image path
  const extractDogInfo = (imagePath) => {
    try {
      // Extract filename from path
      const filename = imagePath.split('/').pop();

      // Parse filename using the DOGNAME-STATEINITIAL format
      const parts = filename.split('.');
      const nameLocation = parts[0]; // Get the part before file extension

      const [dogName, stateInitial] = nameLocation.split('-');

      if (!dogName || !stateInitial) {
        return null;
      }

      // Format the dog name to be title case
      const formattedName = dogName.charAt(0).toUpperCase() + dogName.slice(1).toLowerCase();

      // Map state initials to full state names (add more as needed)
      const stateMap = {
        'AL': 'Alabama',
        'AK': 'Alaska',
        'AZ': 'Arizona',
        'AR': 'Arkansas',
        'CA': 'California',
        'CO': 'Colorado',
        'CT': 'Connecticut',
        'DE': 'Delaware',
        'FL': 'Florida',
        'GA': 'Georgia',
        'HI': 'Hawaii',
        'ID': 'Idaho',
        'IL': 'Illinois',
        'IN': 'Indiana',
        'IA': 'Iowa',
        'KS': 'Kansas',
        'KY': 'Kentucky',
        'LA': 'Louisiana',
        'ME': 'Maine',
        'MD': 'Maryland',
        'MA': 'Massachusetts',
        'MI': 'Michigan',
        'MN': 'Minnesota',
        'MS': 'Mississippi',
        'MO': 'Missouri',
        'MT': 'Montana',
        'NE': 'Nebraska',
        'NV': 'Nevada',
        'NH': 'New Hampshire',
        'NJ': 'New Jersey',
        'NM': 'New Mexico',
        'NY': 'New York',
        'NC': 'North Carolina',
        'ND': 'North Dakota',
        'OH': 'Ohio',
        'OK': 'Oklahoma',
        'OR': 'Oregon',
        'PA': 'Pennsylvania',
        'RI': 'Rhode Island',
        'SC': 'South Carolina',
        'SD': 'South Dakota',
        'TN': 'Tennessee',
        'TX': 'Texas',
        'UT': 'Utah',
        'VT': 'Vermont',
        'VA': 'Virginia',
        'WA': 'Washington',
        'WV': 'West Virginia',
        'WI': 'Wisconsin',
        'WY': 'Wyoming'
      };

      const stateName = stateMap[stateInitial.toUpperCase()] || stateInitial;

      return (
        <>
          <p className="dog-name">{formattedName}</p>
          <p className="dog-location">{stateName}</p>
        </>
      );
    } catch (error) {
      console.error('Error extracting dog info:', error);
      return null;
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 3000); // Change image every 3 seconds
    } else if (interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPlaying, images.length]);

  return (
    <div className={`dog-gallery ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="carousel">
        {images.length > 0 && (
          <div className="carousel-item">
            <img
              src={images[currentIndex]}
              alt={`Gallery image ${currentIndex + 1}`}
              className="carousel-image"
            />
            {showDogInfo && (
              <div className="dog-info">
                {renderImageInfo ? renderImageInfo(images[currentIndex]) : extractDogInfo(images[currentIndex])}
              </div>
            )}
          </div>
        )}
        {showNavigationButtons && (
          <>
            <button className="nav-button prev" onClick={prevImage}>‹</button>
            <button className="nav-button next" onClick={nextImage}>›</button>
          </>
        )}
        {showPlayButton && !isFullscreen && (
          <button className="play-button" onClick={togglePlay}>
            {isPlaying ? '❚❚' : '▶'}
          </button>
        )}
        {showMaximizeButton && !isFullscreen && (
          <button className="fullscreen-button" onClick={enterFullscreen}>⛶</button>
        )}
        {showCloseButton && isFullscreen && (
          <button className="close-button" onClick={exitFullscreen}>×</button>
        )}
        {showPlayButton && isFullscreen && (
          <button className="play-button fullscreen-play" onClick={togglePlay}>
            {isPlaying ? '❚❚' : '▶'}
          </button>
        )}
      </div>

      <style jsx>{`
        .carousel {
          position: relative;
          margin: 0 auto;
        }

        .carousel-item {
          position: relative;
          width: 100%;
        }

        .carousel-image {
          width: 100%;
          height: auto;
          max-height: 400px;
          object-fit: contain;
          border-radius: 8px;
        }
        
        .dog-info {
          background-color:rgba(121, 118, 118, 0);
          padding: 10px;
          text-align: center;
          border-radius: 0 0 8px 8px;
          margin-top: -5px;
        }
        
        .fullscreen .dog-info {
          width: 100%;
          max-width: 500px;
          border-radius: 8px;
          margin-top: 10px;
        }
        
        .dog-name {
          font-weight: bold;
          margin: 0;
          font-size: 1.2rem;
          color: var(--text);
        }
        
        .dog-location {
          margin: 0;
          font-size: 1rem;
          color: var(--text);
        }
        
        .fullscreen .dog-name,
        .fullscreen .dog-location {
          color: #ffffff;
        }

        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-button.prev {
          left: 10px;
        }

        .nav-button.next {
          right: 10px;
        }

        .nav-button:hover {
          background: rgba(0, 0, 0, 0.7);
        }

        .play-button,
        .fullscreen-button,
        .minimize-button {
          position: absolute;
          bottom: 10px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 5px;
          padding: 5px 10px;
          cursor: pointer;
        }

        .play-button {
          left: 10px;
        }

        .fullscreen-button {
          right: 10px;
        }

        .minimize-button {
          right: 10px;
        }

        .fullscreen .carousel {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 1); /* Ensure full black background */
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden; /* Prevent scrolling */
        }

        .fullscreen .carousel-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: auto;
          height: auto;
          max-width: 90vw;
          max-height: 90vh;
        }

        .fullscreen .carousel-image {
          object-fit: contain; /* Adjusted to prevent excessive zooming */
          max-width: 100%;
          max-height: 80vh;
          height: auto;
          border-radius: 0;
        }

        @media (max-width: 768px) {
          .carousel-image {
            max-height: 300px;
          }
        }

        .nav-button,
        .minimize-button {
          width: 60px; /* Increased size */
          height: 60px; /* Increased size */
          font-size: 1.5rem; /* Adjusted font size for better visibility */
        }

        .close-button {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          font-size: 1.5rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
        }

        .close-button:hover {
          background: rgba(0, 0, 0, 0.7);
        }

        .fullscreen-play {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 5px;
          padding: 5px 10px;
          cursor: pointer;
          z-index: 1001;
        }

        .fullscreen-play:hover {
          background: rgba(0, 0, 0, 0.7);
        }
      `}</style>
    </div>
  );
}